import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCheckCircle, FaClock, FaPlus, FaTimes, FaTimesCircle, FaTrashAlt } from "react-icons/fa";
import "../assets/styles/buyer_cm_qc_requirements_modal.scss";

const TABS = [
  { id: "norms", label: "QC Norms" },
  { id: "checklist", label: "Inspection Checklist" },
  { id: "internal", label: "Internal QC Docs" },
  { id: "external", label: "External QC Docs" },
];

const AQL_OPTIONS = [
  { value: "1.0", label: "1.0 (Tight)" },
  { value: "1.5", label: "1.5" },
  { value: "2.5", label: "2.5 (Standard)" },
  { value: "4.0", label: "4.0 (Relaxed)" },
];

const INSPECTION_LEVEL_OPTIONS = [
  { value: "gi", label: "General Level I" },
  { value: "gii", label: "General Level II (Standard)" },
  { value: "giii", label: "General Level III" },
  { value: "s1", label: "Special S-1" },
  { value: "s2", label: "Special S-2" },
  { value: "s3", label: "Special S-3" },
  { value: "s4", label: "Special S-4" },
];

const CHECKLIST_CATEGORY_OPTIONS = [
  { value: "visual", label: "Visual Inspection" },
  { value: "dimension", label: "Dimensional Inspection" },
  { value: "functional", label: "Functional Test" },
  { value: "packaging", label: "Packaging / Labeling" },
  { value: "documentation", label: "Documentation" },
];

/**
 * Buyer sets QC expectations after signing a CM agreement (UI shell; persist via API when backend exists).
 */
export default function BuyerCmQcRequirementsModal({
  open,
  onClose,
  productCode,
  initialData,
  readOnly,
  /** When true (e.g. seller CM), hide "Save as Template" — only apply remains. */
  hideTemplate,
  /** When true: manufacturer copy — "Propose QC Norms" heading, subtext, and approval note. */
  sellerMode,
  /** Buyer reviewing manufacturer-submitted QC (read-only, pending approval). */
  buyerReviewPending,
  /** Manufacturer company name for buyer review copy. */
  manufacturerName,
  /** Buyer reject flow: POST reason and clear pending seller submission. */
  onBuyerRejectRequest,
  /** Buyer approve flow: POST approve-norms and record approval. */
  onBuyerApproveRequest,
  onSave,
  /** Seller CM: RFQ id for navigating to internal inspection report page. */
  sellerInternalInspectionRfqId,
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("norms");
  const [productNameCode, setProductNameCode] = useState("");
  const [aql, setAql] = useState("2.5");
  const [inspectionLevel, setInspectionLevel] = useState("gii");
  const [samplePct, setSamplePct] = useState("10");
  const [minPassPct, setMinPassPct] = useState("98");
  const [standardsNotes, setStandardsNotes] = useState("");
  const [specialRequirements, setSpecialRequirements] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [internalDocs, setInternalDocs] = useState([]);
  const [externalDocs, setExternalDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [prefilledRef, setPrefilledRef] = useState(null);
  const [buyerRejectStep, setBuyerRejectStep] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [inAvail, setInAvail] = useState("");

  const displayCode = useMemo(() => String(productCode || "").trim() || "—", [productCode]);
  const mfrLabel = useMemo(() => String(manufacturerName || "").trim() || "Manufacturer", [manufacturerName]);
  const sellerSubmittedDateLabel = useMemo(() => {
    const raw = initialData?.sellerSubmittedForApprovalAt;
    if (raw == null || raw === "") return "—";
    if (typeof raw !== "string") return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, [initialData]);

  const internalInspRadiosLocked = useMemo(() => {
    const p = initialData;
    if (!p || typeof p !== "object") return false;
    return !!(p.cmInternalInspectionStartedAt || p.cmInternalInspectionReportSubmittedAt);
  }, [initialData]);

  const sellerBuyerDecisionLabel = useMemo(() => {
    const raw = initialData?.buyerQcNormsApprovedAt || initialData?.buyerQcNormsRejectedAt;
    if (raw == null || raw === "") return "—";
    if (typeof raw !== "string") return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, [initialData]);

  useEffect(() => {
    if (!open) return;
    if (!initialData || typeof initialData !== "object") return;
    const p = initialData;
    if (p.cmInternalInspectionAvailable === true) setInAvail("yes");
    else if (p.cmInternalInspectionAvailable === false) setInAvail("no");
    else setInAvail("");
  }, [open, initialData]);

  const formatYmd = (raw) => {
    if (raw == null || raw === "") return "—";
    if (typeof raw !== "string") return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const productValue = useMemo(
    () => String(productNameCode || "").trim() || displayCode,
    [productNameCode, displayCode]
  );

  const checklistPayload = useMemo(() => {
    if (!Array.isArray(checklistItems)) return [];
    return checklistItems.map((it, idx) => ({
      order: idx + 1,
      category: String(it?.category || "").trim(),
      mandatory: !!it?.mandatory,
      description: String(it?.description || "").trim(),
      acceptanceCriteria: String(it?.acceptanceCriteria || "").trim(),
    }));
  }, [checklistItems]);

  const internalDocsPayload = useMemo(() => {
    if (!Array.isArray(internalDocs)) return [];
    return internalDocs.map((d, idx) => ({
      order: idx + 1,
      name: String(d?.name || "").trim(),
      fileFormat: String(d?.fileFormat || "").trim(),
      required: !!d?.required,
    }));
  }, [internalDocs]);

  const externalDocsPayload = useMemo(() => {
    if (!Array.isArray(externalDocs)) return [];
    return externalDocs.map((d, idx) => ({
      order: idx + 1,
      name: String(d?.name || "").trim(),
      fileFormat: String(d?.fileFormat || "").trim(),
      required: !!d?.required,
    }));
  }, [externalDocs]);

  useEffect(() => {
    if (!open) return;
    if (productNameCode) return;
    if (displayCode === "—") return;
    setProductNameCode(displayCode);
  }, [open, productNameCode, displayCode]);

  useEffect(() => {
    if (!open) return;
    if (!initialData || typeof initialData !== "object") return;
    const key = `${displayCode}:${JSON.stringify(initialData).length}`;
    if (prefilledRef === key) return;
    setPrefilledRef(key);
    const p = initialData || {};
    if (typeof p.productNameCode === "string" && p.productNameCode.trim()) setProductNameCode(p.productNameCode.trim());
    if (typeof p.aql === "string" && p.aql.trim()) setAql(p.aql.trim());
    if (typeof p.inspectionLevel === "string" && p.inspectionLevel.trim()) setInspectionLevel(p.inspectionLevel.trim());
    if (p.samplePct != null) setSamplePct(String(p.samplePct));
    if (p.minPassPct != null) setMinPassPct(String(p.minPassPct));
    if (typeof p.standardsNotes === "string") setStandardsNotes(p.standardsNotes);
    if (typeof p.specialRequirements === "string") setSpecialRequirements(p.specialRequirements);
    if (Array.isArray(p.inspectionChecklistItems)) {
      setChecklistItems(
        p.inspectionChecklistItems.map((it, idx) => ({
          id: it?.id || `${Date.now()}-${idx}`,
          category: it?.category || "visual",
          mandatory: it?.mandatory !== false,
          description: it?.description || "",
          acceptanceCriteria: it?.acceptanceCriteria || "",
        }))
      );
    }
    if (Array.isArray(p.internalQcDocs)) {
      setInternalDocs(
        p.internalQcDocs.map((d, idx) => ({
          id: d?.id || `${Date.now()}-i-${idx}`,
          name: d?.name || "",
          fileFormat: d?.fileFormat || "pdf",
          required: d?.required !== false,
        }))
      );
    }
    if (Array.isArray(p.externalQcDocs)) {
      setExternalDocs(
        p.externalQcDocs.map((d, idx) => ({
          id: d?.id || `${Date.now()}-e-${idx}`,
          name: d?.name || "",
          fileFormat: d?.fileFormat || "pdf",
          required: d?.required !== false,
        }))
      );
    }
  }, [open, initialData, prefilledRef, displayCode]);

  useEffect(() => {
    if (!open) {
      setBuyerRejectStep(false);
      setRejectReason("");
    }
  }, [open]);

  if (!open) return null;

  async function handleBuyerRejectSubmit() {
    const reason = String(rejectReason || "").trim();
    if (!reason) return;
    if (typeof onBuyerRejectRequest === "function") {
      setSaving(true);
      try {
        await onBuyerRejectRequest(reason);
        onClose?.();
      } catch {
        /* parent shows error toast */
      } finally {
        setSaving(false);
      }
      return;
    }
    onClose?.();
  }

  async function handleBuyerApproveSubmit() {
    if (typeof onBuyerApproveRequest === "function") {
      setSaving(true);
      try {
        await onBuyerApproveRequest();
        onClose?.();
      } catch {
        /* parent shows error toast */
      } finally {
        setSaving(false);
      }
      return;
    }
    onClose?.();
  }

  async function handleInspectionUpdate(patch) {
    if (typeof onSave !== "function") return;
    setSaving(true);
    try {
      await onSave({ action: "inspectionUpdate", payload: patch });
    } finally {
      setSaving(false);
    }
  }

  async function handleSave(action) {
    const payload = {
      productNameCode: String(productValue || "").trim(),
      aql,
      inspectionLevel,
      samplePct: String(samplePct ?? "").trim(),
      minPassPct: String(minPassPct ?? "").trim(),
      standardsNotes: standardsNotes.trim(),
      specialRequirements: specialRequirements.trim(),
      inspectionChecklistItems: checklistPayload,
      internalQcDocs: internalDocsPayload,
      externalQcDocs: externalDocsPayload,
      action: action || "apply",
    };
    if (typeof onSave === "function") {
      setSaving(true);
      try {
        await onSave(payload);
      } finally {
        setSaving(false);
      }
      return;
    }
    onClose?.();
  }

  return (
    <div
      className="cm-qc-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={`cm-qc-modal-card${buyerReviewPending && readOnly ? " cm-qc-modal-card--buyer-review" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-qc-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="cm-qc-modal-close" onClick={() => onClose?.()} aria-label="Close">
          <FaTimes />
        </button>

        {buyerReviewPending && readOnly && buyerRejectStep ? (
          <>
            <h2 id="cm-qc-modal-title" className="cm-qc-modal-title">
              Request changes
            </h2>
            <p className="cm-qc-modal-desc">
              Provide feedback so the manufacturer can revise their QC norms proposal.
            </p>
            <div className="cm-qc-reject-panel">
              <label className="cm-qc-reject-panel__label" htmlFor="cm-qc-reject-reason">
                Rejection Reason (required if rejecting)
              </label>
              <textarea
                id="cm-qc-reject-reason"
                className="cm-qc-reject-panel__textarea"
                rows={7}
                placeholder="Provide detailed feedback if you need to reject these QC norms..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={saving}
              />
              <p className="cm-qc-reject-panel__foot">
                Your feedback will be sent to the manufacturer to help them revise their proposal.
              </p>
            </div>
          </>
        ) : (
          <>
            <h2 id="cm-qc-modal-title" className="cm-qc-modal-title">
              {sellerMode
                ? `Propose QC Norms - ${displayCode}`
                : buyerReviewPending && readOnly
                  ? "Review Manufacturer's QC Norms Proposal"
                  : `Set QC Requirements - ${displayCode}`}
            </h2>
        <p className="cm-qc-modal-desc">
          {sellerMode ? (
            <>
              As the manufacturer, propose QC norms and standards for buyer review and approval. You can also view
              buyer-defined requirements.
            </>
          ) : buyerReviewPending && readOnly ? (
            <>
              Review and approve the quality control standards proposed by {mfrLabel} for {displayCode}.
            </>
          ) : (
            <>
              As the buyer, define quality control standards, checklists, and document requirements. These will be shared
              with manufacturer and external QC agencies.
            </>
          )}
        </p>
        {buyerReviewPending && readOnly ? (
          <div className="cm-qc-modal-buyer-review-banner" role="status">
            <span className="cm-qc-modal-buyer-review-banner__icon" aria-hidden>
              <FaClock />
            </span>
            <div>
              <div className="cm-qc-modal-buyer-review-banner__title">Pending Your Approval</div>
              <p className="cm-qc-modal-buyer-review-banner__text">
                Submitted by {mfrLabel} on {sellerSubmittedDateLabel}. Please review all sections carefully before
                approving or rejecting this proposal.
              </p>
            </div>
          </div>
        ) : null}
        {sellerMode && initialData?.buyerQcNormsApprovedAt ? (
          <div className="cm-qc-modal-status cm-qc-modal-status--approved" role="status">
            QC norms approved by buyer on {sellerBuyerDecisionLabel}.
          </div>
        ) : null}
        {sellerMode && initialData?.buyerQcNormsRejectedAt ? (
          <div className="cm-qc-modal-status cm-qc-modal-status--rejected" role="status">
            <div className="cm-qc-modal-status__title">Changes requested by buyer</div>
            <div className="cm-qc-modal-status__meta">Sent on {sellerBuyerDecisionLabel}</div>
            {String(initialData?.buyerQcNormsRejectionReason || "").trim() ? (
              <div className="cm-qc-modal-status__reason">
                <strong>Reason:</strong> {String(initialData.buyerQcNormsRejectionReason).trim()}
              </div>
            ) : null}
          </div>
        ) : null}
        {sellerMode && initialData?.buyerQcNormsApprovedAt ? (
          <div className="cm-qc-inspection">
            <div className="cm-qc-inspection__title">Internal inspection</div>
            {initialData?.cmInternalInspectionStartedAt && !initialData?.cmInternalInspectionReportSubmittedAt ? (
              <div className="cm-qc-inspection__status-banner" role="status">
                <FaCheckCircle className="cm-qc-inspection__status-icon" aria-hidden />
                <div className="cm-qc-inspection__status-body">
                  <div className="cm-qc-inspection__status-title">Inspection started</div>
                  <div className="cm-qc-inspection__status-meta">
                    Started on {formatYmd(initialData.cmInternalInspectionStartedAt)} · In progress — submit your report
                    when ready.
                  </div>
                </div>
              </div>
            ) : null}
            <p className="cm-qc-inspection__q">Is internal inspection available at your facility?</p>
            <div className="cm-qc-inspection__row" role="group" aria-label="Internal inspection available">
              <label
                className={`cm-qc-inspection__choice${internalInspRadiosLocked ? " cm-qc-inspection__choice--locked" : ""}`}
              >
                <input
                  type="radio"
                  name="cm-internal-insp"
                  checked={inAvail === "yes"}
                  disabled={saving || internalInspRadiosLocked}
                  onChange={() => {
                    setInAvail("yes");
                    handleInspectionUpdate({ cmInternalInspectionAvailable: true });
                  }}
                />
                Yes
              </label>
              <label
                className={`cm-qc-inspection__choice${internalInspRadiosLocked ? " cm-qc-inspection__choice--locked" : ""}`}
              >
                <input
                  type="radio"
                  name="cm-internal-insp"
                  checked={inAvail === "no"}
                  disabled={saving || internalInspRadiosLocked}
                  onChange={() => {
                    setInAvail("no");
                    handleInspectionUpdate({
                      cmInternalInspectionAvailable: false,
                      cmInternalInspectionStartedAt: null,
                      cmInternalInspectionReportSubmittedAt: null,
                      cmInternalInspectionReportNotes: "",
                    });
                  }}
                />
                No
              </label>
            </div>
            {internalInspRadiosLocked ? (
              <p className="cm-qc-inspection__lock-hint">Availability is locked after inspection has started.</p>
            ) : null}
            {inAvail === "yes" && !initialData?.cmInternalInspectionStartedAt ? (
              <button
                type="button"
                className="cm-qc-btn cm-qc-btn--primary cm-qc-btn--dark cm-qc-inspection__cta"
                disabled={saving}
                onClick={() => handleInspectionUpdate({ cmInternalInspectionStartedAt: new Date().toISOString() })}
              >
                {saving ? "Starting…" : "Start inspection"}
              </button>
            ) : null}
            {inAvail === "yes" && initialData?.cmInternalInspectionStartedAt ? (
              <div className="cm-qc-inspection__report">
                {initialData?.cmInternalInspectionReportSubmittedAt ? (
                  <div className="cm-qc-modal-status cm-qc-modal-status--approved cm-qc-inspection__done" role="status">
                    Report submitted on {formatYmd(initialData.cmInternalInspectionReportSubmittedAt)}.
                    {String(initialData?.cmInternalInspectionReportNotes || "").trim() ? (
                      <div className="cm-qc-inspection__report-view">
                        <strong>Notes:</strong> {String(initialData.cmInternalInspectionReportNotes).trim()}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="cm-qc-btn cm-qc-btn--primary cm-qc-btn--dark cm-qc-inspection__cta"
                  disabled={saving}
                  onClick={() => {
                    const id = Number(sellerInternalInspectionRfqId);
                    if (!Number.isFinite(id) || id <= 0) {
                      toast.error("RFQ is missing — close and open QC requirements again.");
                      return;
                    }
                    onClose?.();
                    navigate(`/seller/cm-rfqs/${id}/internal-inspection-report`);
                  }}
                >
                  {initialData?.cmInternalInspectionReportSubmittedAt ? "View report" : "Submit report"}
                </button>
              </div>
            ) : null}
            {inAvail === "no" ? (
              <p className="cm-qc-inspection__hint">Internal inspection was marked as not available.</p>
            ) : null}
          </div>
        ) : null}
        {sellerMode && !readOnly ? (
          <div className="cm-qc-modal-seller-note" role="note">
            <strong>Note:</strong> Any changes you make will be sent to the buyer for approval. The buyer has final
            authority to approve or modify these QC norms.
          </div>
        ) : null}

        <div className="cm-qc-modal-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={`cm-qc-modal-tab ${activeTab === t.id ? "cm-qc-modal-tab--active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {buyerReviewPending && readOnly && t.id === "norms" ? "Overview" : t.label}
            </button>
          ))}
        </div>

        <div className="cm-qc-modal-body">
          {activeTab === "norms" && (
            <section className="cm-qc-panel" aria-labelledby="cm-qc-general-heading">
              <h3 id="cm-qc-general-heading" className="cm-qc-panel-title">
                General QC Standards
              </h3>

              <div className="cm-qc-field">
                <label htmlFor="cm-qc-product">Product Name/Code</label>
                <input
                  id="cm-qc-product"
                  type="text"
                  className="cm-qc-input"
                  value={productValue}
                  onChange={(e) => setProductNameCode(e.target.value)}
                  placeholder="Enter product name/code"
                  disabled={!!readOnly}
                />
              </div>

              <div className="cm-qc-form-grid">
                <div className="cm-qc-field">
                  <label htmlFor="cm-qc-aql">Acceptable Quality Level (AQL)</label>
                  <select
                    id="cm-qc-aql"
                    className="cm-qc-input"
                    value={aql}
                    onChange={(e) => setAql(e.target.value)}
                    disabled={!!readOnly}
                  >
                    {AQL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cm-qc-field">
                  <label htmlFor="cm-qc-insp">Inspection Level</label>
                  <select
                    id="cm-qc-insp"
                    className="cm-qc-input"
                    value={inspectionLevel}
                    onChange={(e) => setInspectionLevel(e.target.value)}
                    disabled={!!readOnly}
                  >
                    {INSPECTION_LEVEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cm-qc-field">
                  <label htmlFor="cm-qc-sample">Sample Size (%)</label>
                  <input
                    id="cm-qc-sample"
                    type="number"
                    className="cm-qc-input"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    step={1}
                    value={samplePct}
                    onChange={(e) => setSamplePct(e.target.value)}
                    disabled={!!readOnly}
                  />
                </div>
                <div className="cm-qc-field">
                  <label htmlFor="cm-qc-pass">Minimum Pass Rate (%)</label>
                  <input
                    id="cm-qc-pass"
                    type="number"
                    className="cm-qc-input"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    step={1}
                    value={minPassPct}
                    onChange={(e) => setMinPassPct(e.target.value)}
                    disabled={!!readOnly}
                  />
                </div>
              </div>

              <div className="cm-qc-field">
                <label htmlFor="cm-qc-standards">Quality Standards &amp; Certifications</label>
                <textarea
                  id="cm-qc-standards"
                  className="cm-qc-textarea"
                  rows={4}
                  placeholder="e.g., ISO 9001, ISO 13485, FDA, CE Mark, RoHS, REACH compliance..."
                  value={standardsNotes}
                  onChange={(e) => setStandardsNotes(e.target.value)}
                  disabled={!!readOnly}
                />
              </div>

              <div className="cm-qc-field">
                <label htmlFor="cm-qc-special">Special Requirements</label>
                <textarea
                  id="cm-qc-special"
                  className="cm-qc-textarea"
                  rows={4}
                  placeholder="Any specific quality requirements, testing conditions, or acceptance criteria..."
                  value={specialRequirements}
                  onChange={(e) => setSpecialRequirements(e.target.value)}
                  disabled={!!readOnly}
                />
              </div>
            </section>
          )}

          {activeTab === "checklist" && (
            <section className="cm-qc-checklist">
              <div className="cm-qc-checklist-head">
                <div>
                  <div className="cm-qc-checklist-title">
                    {buyerReviewPending && readOnly ? "Inspection Checklist" : "Inspection Checklist Items"}
                  </div>
                  <div className="cm-qc-checklist-sub">
                    {buyerReviewPending && readOnly
                      ? `${Array.isArray(checklistItems) ? checklistItems.length : 0} inspection items proposed`
                      : "Define specific items to be checked during quality inspection"}
                  </div>
                </div>
                {!buyerReviewPending ? (
                  <button
                    type="button"
                    className="cm-qc-add-item"
                    onClick={() => {
                      if (readOnly) return;
                      setChecklistItems((prev) => [
                        ...(Array.isArray(prev) ? prev : []),
                        {
                          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                          category: "visual",
                          mandatory: true,
                          description: "",
                          acceptanceCriteria: "",
                        },
                      ]);
                    }}
                    disabled={!!readOnly}
                  >
                    <FaPlus aria-hidden /> Add Item
                  </button>
                ) : null}
              </div>

              {Array.isArray(checklistItems) && checklistItems.length > 0 ? (
                <div className="cm-qc-checklist-items">
                  {buyerReviewPending && readOnly
                    ? checklistItems.map((it, idx) => {
                        const cat = String(it?.category || "visual");
                        const catLabel =
                          CHECKLIST_CATEGORY_OPTIONS.find((o) => String(o.value) === cat)?.label || "Visual Inspection";
                        return (
                          <div className="cm-qc-review-item" key={it.id || idx}>
                            <div className="cm-qc-review-item__top">
                              <span className="cm-qc-review-chip">{catLabel}</span>
                              {!!it?.mandatory ? (
                                <span className="cm-qc-review-pill cm-qc-review-pill--mandatory">Mandatory</span>
                              ) : null}
                            </div>
                            <div className="cm-qc-review-item__desc">{String(it?.description || "").trim() || "—"}</div>
                            <div className="cm-qc-review-item__ac">
                              <span className="cm-qc-review-item__ac-label">Acceptance Criteria:</span>{" "}
                              {String(it?.acceptanceCriteria || "").trim() || "—"}
                            </div>
                          </div>
                        );
                      })
                    : checklistItems.map((it, idx) => (
                        <div className="cm-qc-item-card" key={it.id || idx}>
                          <div className="cm-qc-item-top">
                            <span className="cm-qc-item-chip">Item {idx + 1}</span>
                            <button
                              type="button"
                              className="cm-qc-item-delete"
                              aria-label="Delete item"
                              title="Delete item"
                              onClick={() => {
                                if (readOnly) return;
                                setChecklistItems((prev) =>
                                  Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []
                                );
                              }}
                              disabled={!!readOnly}
                            >
                              <FaTrashAlt aria-hidden />
                            </button>
                          </div>

                          <div className="cm-qc-item-grid">
                            <div className="cm-qc-field">
                              <label htmlFor={`cm-qc-cat-${idx}`}>
                                Category <span className="cm-qc-req">*</span>
                              </label>
                              <select
                                id={`cm-qc-cat-${idx}`}
                                className="cm-qc-input"
                                value={String(it.category || "visual")}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setChecklistItems((prev) =>
                                    (Array.isArray(prev) ? prev : []).map((x, i) =>
                                      i === idx ? { ...x, category: v } : x
                                    )
                                  );
                                }}
                                disabled={!!readOnly}
                              >
                                {CHECKLIST_CATEGORY_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="cm-qc-mandatory">
                              <label className="cm-qc-checkbox">
                                <input
                                  type="checkbox"
                                  checked={!!it.mandatory}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setChecklistItems((prev) =>
                                      (Array.isArray(prev) ? prev : []).map((x, i) =>
                                        i === idx ? { ...x, mandatory: checked } : x
                                      )
                                    );
                                  }}
                                  disabled={!!readOnly}
                                />
                                <span>Mandatory Check</span>
                              </label>
                            </div>
                          </div>

                          <div className="cm-qc-field">
                            <label htmlFor={`cm-qc-desc-${idx}`}>
                              Description <span className="cm-qc-req">*</span>
                            </label>
                            <input
                              id={`cm-qc-desc-${idx}`}
                              type="text"
                              className="cm-qc-input"
                              placeholder="Describe what needs to be checked"
                              value={String(it.description || "")}
                              onChange={(e) => {
                                const v = e.target.value;
                                setChecklistItems((prev) =>
                                  (Array.isArray(prev) ? prev : []).map((x, i) =>
                                    i === idx ? { ...x, description: v } : x
                                  )
                                );
                              }}
                              disabled={!!readOnly}
                            />
                          </div>

                          <div className="cm-qc-field">
                            <label htmlFor={`cm-qc-acc-${idx}`}>
                              Acceptance Criteria <span className="cm-qc-req">*</span>
                            </label>
                            <textarea
                              id={`cm-qc-acc-${idx}`}
                              className="cm-qc-textarea"
                              rows={3}
                              placeholder="Define pass/fail criteria"
                              value={String(it.acceptanceCriteria || "")}
                              onChange={(e) => {
                                const v = e.target.value;
                                setChecklistItems((prev) =>
                                  (Array.isArray(prev) ? prev : []).map((x, i) =>
                                    i === idx ? { ...x, acceptanceCriteria: v } : x
                                  )
                                );
                              }}
                              disabled={!!readOnly}
                            />
                          </div>
                        </div>
                      ))}
                </div>
              ) : (
                <div className="cm-qc-empty">
                  {buyerReviewPending && readOnly ? (
                    <>No inspection checklist items proposed.</>
                  ) : (
                    <>
                      No checklist items yet. Click <strong>Add Item</strong> to start.
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === "internal" && (
            <section className="cm-qc-docs">
              <div className="cm-qc-docs-head">
                <div>
                  <div className="cm-qc-docs-title">
                    {buyerReviewPending && readOnly ? "Internal QC Docs" : "Internal QC Document Requirements"}
                  </div>
                  <div className="cm-qc-docs-sub">
                    {buyerReviewPending && readOnly
                      ? "Documents required from manufacturer's internal QC team"
                      : "Specify documents required from manufacturer\u2019s internal QC process"}
                  </div>
                </div>
                {!buyerReviewPending ? (
                  <button
                    type="button"
                    className="cm-qc-add-item"
                    onClick={() => {
                      if (readOnly) return;
                      setInternalDocs((prev) => [
                        ...(Array.isArray(prev) ? prev : []),
                        {
                          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                          name: "",
                          fileFormat: "pdf",
                          required: true,
                        },
                      ]);
                    }}
                    disabled={!!readOnly}
                  >
                    <FaPlus aria-hidden /> Add Document
                  </button>
                ) : null}
              </div>

              {Array.isArray(internalDocs) && internalDocs.length > 0 ? (
                <div className="cm-qc-checklist-items">
                  {buyerReviewPending && readOnly
                    ? internalDocs.map((d, idx) => (
                        <div className="cm-qc-review-doc" key={d.id || idx}>
                          <div className="cm-qc-review-doc__row">
                            <div>
                              <div className="cm-qc-review-doc__name">{String(d?.name || "").trim() || "—"}</div>
                              <div className="cm-qc-review-doc__meta">
                                Format: {String(d?.fileFormat || "").trim() ? String(d.fileFormat).toUpperCase() : "—"}
                              </div>
                            </div>
                            <span className={`cm-qc-review-doc__pill ${d?.required ? "is-required" : "is-optional"}`}>
                              {d?.required ? "Required" : "Optional"}
                            </span>
                          </div>
                        </div>
                      ))
                    : internalDocs.map((d, idx) => (
                        <div className="cm-qc-item-card" key={d.id || idx}>
                          <div className="cm-qc-item-top">
                            <span className="cm-qc-item-chip">Document {idx + 1}</span>
                            <button
                              type="button"
                              className="cm-qc-item-delete"
                              aria-label="Delete document"
                              title="Delete document"
                              onClick={() => {
                                if (readOnly) return;
                                setInternalDocs((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []));
                              }}
                              disabled={!!readOnly}
                            >
                              <FaTrashAlt aria-hidden />
                            </button>
                          </div>

                          <div className="cm-qc-docs-grid">
                            <div className="cm-qc-field">
                              <label htmlFor={`cm-qc-int-name-${idx}`}>
                                Document Name <span className="cm-qc-req">*</span>
                              </label>
                              <input
                                id={`cm-qc-int-name-${idx}`}
                                type="text"
                                className="cm-qc-input"
                                placeholder="Enter document name"
                                value={String(d.name || "")}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setInternalDocs((prev) =>
                                    (Array.isArray(prev) ? prev : []).map((x, i) => (i === idx ? { ...x, name: v } : x))
                                  );
                                }}
                                disabled={!!readOnly}
                              />
                            </div>
                            <div className="cm-qc-field">
                              <label htmlFor={`cm-qc-int-format-${idx}`}>File Format</label>
                              <select
                                id={`cm-qc-int-format-${idx}`}
                                className="cm-qc-input"
                                value={String(d.fileFormat || "pdf")}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setInternalDocs((prev) =>
                                    (Array.isArray(prev) ? prev : []).map((x, i) =>
                                      i === idx ? { ...x, fileFormat: v } : x
                                    )
                                  );
                                }}
                                disabled={!!readOnly}
                              >
                                <option value="pdf">PDF</option>
                                <option value="doc">DOC</option>
                                <option value="docx">DOCX</option>
                                <option value="xls">XLS</option>
                                <option value="xlsx">XLSX</option>
                                <option value="jpg">JPG</option>
                                <option value="png">PNG</option>
                              </select>
                            </div>
                          </div>

                          <label className="cm-qc-checkbox cm-qc-doc-required">
                            <input
                              type="checkbox"
                              checked={!!d.required}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setInternalDocs((prev) =>
                                  (Array.isArray(prev) ? prev : []).map((x, i) =>
                                    i === idx ? { ...x, required: checked } : x
                                  )
                                );
                              }}
                              disabled={!!readOnly}
                            />
                            <span>Required Document (Must be submitted)</span>
                          </label>
                        </div>
                      ))}
                </div>
              ) : (
                <div className="cm-qc-empty">
                  {buyerReviewPending && readOnly ? (
                    <>No internal QC documents proposed.</>
                  ) : (
                    <>
                      No documents added yet. Click <strong>Add Document</strong> to start.
                    </>
                  )}
                </div>
              )}

              <div className="cm-qc-info-box">
                <strong>Common Internal QC Documents:</strong> Inspection Report, Test Results, Measurement Data, Quality
                Control Photos, Defect Analysis Report, Statistical Process Control Charts
              </div>
            </section>
          )}

          {activeTab === "external" && (
            <section className="cm-qc-docs">
              <div className="cm-qc-docs-head">
                <div>
                  <div className="cm-qc-docs-title">
                    {buyerReviewPending && readOnly ? "External QC Docs" : "External QC Document Requirements"}
                  </div>
                  <div className="cm-qc-docs-sub">
                    {buyerReviewPending && readOnly
                      ? "Documents required from external QC agencies"
                      : "Specify documents required from third-party inspection agencies"}
                  </div>
                </div>
                {!buyerReviewPending ? (
                  <button
                    type="button"
                    className="cm-qc-add-item"
                    onClick={() => {
                      if (readOnly) return;
                      setExternalDocs((prev) => [
                        ...(Array.isArray(prev) ? prev : []),
                        {
                          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                          name: "",
                          fileFormat: "pdf",
                          required: true,
                        },
                      ]);
                    }}
                    disabled={!!readOnly}
                  >
                    <FaPlus aria-hidden /> Add Document
                  </button>
                ) : null}
              </div>

              {Array.isArray(externalDocs) && externalDocs.length > 0 ? (
                <div className="cm-qc-checklist-items">
                  {buyerReviewPending && readOnly
                    ? externalDocs.map((d, idx) => (
                        <div className="cm-qc-review-doc" key={d.id || idx}>
                          <div className="cm-qc-review-doc__row">
                            <div>
                              <div className="cm-qc-review-doc__name">{String(d?.name || "").trim() || "—"}</div>
                              <div className="cm-qc-review-doc__meta">
                                Format: {String(d?.fileFormat || "").trim() ? String(d.fileFormat).toUpperCase() : "—"}
                              </div>
                            </div>
                            <span className={`cm-qc-review-doc__pill ${d?.required ? "is-required" : "is-optional"}`}>
                              {d?.required ? "Required" : "Optional"}
                            </span>
                          </div>
                        </div>
                      ))
                    : externalDocs.map((d, idx) => (
                        <div className="cm-qc-item-card" key={d.id || idx}>
                          <div className="cm-qc-item-top">
                            <span className="cm-qc-item-chip">Document {idx + 1}</span>
                            <button
                              type="button"
                              className="cm-qc-item-delete"
                              aria-label="Delete document"
                              title="Delete document"
                              onClick={() => {
                                if (readOnly) return;
                                setExternalDocs((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []));
                              }}
                              disabled={!!readOnly}
                            >
                              <FaTrashAlt aria-hidden />
                            </button>
                          </div>

                          <div className="cm-qc-docs-grid">
                            <div className="cm-qc-field">
                              <label htmlFor={`cm-qc-ext-name-${idx}`}>
                                Document Name <span className="cm-qc-req">*</span>
                              </label>
                              <input
                                id={`cm-qc-ext-name-${idx}`}
                                type="text"
                                className="cm-qc-input"
                                placeholder="Enter document name"
                                value={String(d.name || "")}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setExternalDocs((prev) =>
                                    (Array.isArray(prev) ? prev : []).map((x, i) => (i === idx ? { ...x, name: v } : x))
                                  );
                                }}
                                disabled={!!readOnly}
                              />
                            </div>
                            <div className="cm-qc-field">
                              <label htmlFor={`cm-qc-ext-format-${idx}`}>File Format</label>
                              <select
                                id={`cm-qc-ext-format-${idx}`}
                                className="cm-qc-input"
                                value={String(d.fileFormat || "pdf")}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setExternalDocs((prev) =>
                                    (Array.isArray(prev) ? prev : []).map((x, i) =>
                                      i === idx ? { ...x, fileFormat: v } : x
                                    )
                                  );
                                }}
                                disabled={!!readOnly}
                              >
                                <option value="pdf">PDF</option>
                                <option value="doc">DOC</option>
                                <option value="docx">DOCX</option>
                                <option value="xls">XLS</option>
                                <option value="xlsx">XLSX</option>
                                <option value="jpg">JPG</option>
                                <option value="png">PNG</option>
                              </select>
                            </div>
                          </div>

                          <label className="cm-qc-checkbox cm-qc-doc-required">
                            <input
                              type="checkbox"
                              checked={!!d.required}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setExternalDocs((prev) =>
                                  (Array.isArray(prev) ? prev : []).map((x, i) =>
                                    i === idx ? { ...x, required: checked } : x
                                  )
                                );
                              }}
                              disabled={!!readOnly}
                            />
                            <span>Required Document (Must be submitted)</span>
                          </label>
                        </div>
                      ))}
                </div>
              ) : (
                <div className="cm-qc-empty">
                  {buyerReviewPending && readOnly ? (
                    <>No external QC documents proposed.</>
                  ) : (
                    <>
                      No documents added yet. Click <strong>Add Document</strong> to start.
                    </>
                  )}
                </div>
              )}

              <div className="cm-qc-info-box">
                <strong>Common External QC Documents:</strong> Third-Party Inspection Certificate, Lab Test Results,
                Compliance Certificates, Material Test Reports, Safety Certification, Calibration Certificates
              </div>
            </section>
          )}
        </div>
          </>
        )}

        <div className="cm-qc-modal-footer">
          <div className="cm-qc-modal-footer-left">
            <button
              type="button"
              className="cm-qc-btn cm-qc-btn--secondary"
              onClick={() => {
                if (buyerReviewPending && readOnly && buyerRejectStep) {
                  setBuyerRejectStep(false);
                  setRejectReason("");
                } else {
                  onClose?.();
                }
              }}
              disabled={saving}
            >
              {buyerReviewPending && readOnly && buyerRejectStep
                ? "Back"
                : buyerReviewPending && readOnly
                  ? "Review Later"
                  : readOnly
                    ? "Close"
                    : "Cancel"}
            </button>
          </div>
          {buyerReviewPending && readOnly && buyerRejectStep ? (
            <div className="cm-qc-modal-footer-right">
              <button
                type="button"
                className="cm-qc-btn cm-qc-btn--primary cm-qc-btn--dark"
                onClick={() => handleBuyerRejectSubmit()}
                disabled={saving || !String(rejectReason || "").trim()}
              >
                {saving ? "Sending…" : "Send to manufacturer"}
              </button>
            </div>
          ) : buyerReviewPending && readOnly ? (
            <div className="cm-qc-modal-footer-right">
              <button
                type="button"
                className="cm-qc-btn cm-qc-btn--danger"
                onClick={() => setBuyerRejectStep(true)}
                disabled={saving}
              >
                <FaTimesCircle aria-hidden /> Reject &amp; Request Changes
              </button>
              <button
                type="button"
                className="cm-qc-btn cm-qc-btn--success"
                onClick={() => handleBuyerApproveSubmit()}
                disabled={saving}
              >
                <FaCheckCircle aria-hidden /> {saving ? "Approving…" : "Approve QC Norms"}
              </button>
            </div>
          ) : !readOnly ? (
            <div className="cm-qc-modal-footer-right">
              {!hideTemplate || sellerMode ? (
                <button
                  type="button"
                  className="cm-qc-btn cm-qc-btn--secondary"
                  onClick={() => handleSave("template")}
                  disabled={saving}
                >
                  Save as Template
                </button>
              ) : null}
              <button
                type="button"
                className="cm-qc-btn cm-qc-btn--primary cm-qc-btn--dark"
                onClick={() => handleSave("apply")}
                disabled={saving}
              >
                {saving
                  ? sellerMode
                    ? "Submitting…"
                    : "Saving…"
                  : sellerMode
                    ? "Submit for Buyer Approval"
                    : "Save & Apply to Order"}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
